import type { PermissionCategory } from "../../domain/entities/Profile";
import type {
  UserPermissionItemDTO,
  UserPermissionsResponseDTO,
} from "../../domain/dto/ProfileDTO";

export const flattenUserPermissions = (
  response: UserPermissionsResponseDTO,
): UserPermissionItemDTO[] => {
  const { data } = response;

  if (Array.isArray(data.permissions) && data.permissions.length > 0) {
    return data.permissions;
  }

  return (data.categories ?? []).flatMap((category) => category.permissions);
};

export const flattenUserPermissionSlugs = (
  response: UserPermissionsResponseDTO,
): string[] => flattenUserPermissions(response).map((permission) => permission.slug);

export const readUserRoleName = (
  response: UserPermissionsResponseDTO,
): string | undefined => {
  const roleName = response.data.role_name?.trim();
  return roleName || undefined;
};

export const mapGroupedUserPermissions = (
  response: UserPermissionsResponseDTO,
): PermissionCategory[] => {
  const { data } = response;

  if (Array.isArray(data.categories) && data.categories.length > 0) {
    return data.categories
      .map((category) => ({
        title: category.category,
        items: category.permissions.map((permission) => ({
          label: permission.generic_name || permission.slug,
          description: permission.description,
        })),
      }))
      .filter((group) => group.items.length > 0);
  }

  const flatPermissions = flattenUserPermissions(response);
  if (flatPermissions.length === 0) {
    return [];
  }

  return [
    {
      title: "Permissions",
      items: flatPermissions.map((permission) => ({
        label: permission.generic_name || permission.slug,
        description: permission.description,
      })),
    },
  ];
};

export interface UserPermissionsResult {
  permissionGroups: PermissionCategory[];
  permissions: UserPermissionItemDTO[];
  roleName?: string;
}

export const mapUserPermissionsResponse = (
  response: UserPermissionsResponseDTO,
): UserPermissionsResult => ({
  permissionGroups: mapGroupedUserPermissions(response),
  permissions: flattenUserPermissions(response),
  roleName: readUserRoleName(response),
});
